/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import {
  darken,
  alpha,
  lighten,
  createTheme as createMuiTheme,
} from '@mui/material/styles';
import { IndeterminateCheckBoxSharp } from '@mui/icons-material';
import { merge } from 'lodash';
import { Tooltip } from '@mui/material';

import { THEME_BL } from 'consts';
import RadioIcon from 'components/mui/RadioIcon';
import CheckIcon from 'components/mui/CheckIcon';
import StepIcon from 'components/mui/StepIcon';
import BlIcon from 'components/icons/BlIcon';

const { REACT_APP_SEO_COMPANY_NAME, REACT_APP_SEO_FAVICON } = process.env;

const shadows = [
  'none',
  '0px 2px 10px rgba(0, 0, 0, 0.1);',
  '0 1px 10px -5px rgba(0, 0, 0, 0.5)',
  '0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
];

const initialBreakpoints = {
  values: {
    // Sadly, but "0" breakpoint should always be named "xs"
    xs: 0,
    // This is custom breakpoint, I'm not sure how it's gonna work
    // with Grid/Container components.
    pt: 415,
    sm: 768,
    md: 1024,
    lg: 1280,
    xl: 1600,
  },
};

const createTheme = (customTheme) => {
  const { type = 'light' } = customTheme || {};

  const rootPropsMerged = merge(
    {
      id: 'default',
      custom: {
        companyName: REACT_APP_SEO_COMPANY_NAME,
        favicons: {
          ico: REACT_APP_SEO_FAVICON,
        },
      },
      spacing: 4,
    },
    customTheme,
  );

  const { custom: rootCustom, ...rootProps } = rootPropsMerged;

  const { breakpoints, palette, ...defaultTheme } = createMuiTheme({
    breakpoints: initialBreakpoints,
    palette: merge(
      {
        // type,
        mode: type,
        // primary: {
        //   main: '#673ab7',
        // },
        background: {
          default: '#f5f8fb',
        },
        dark: {
          main: '#111',
        },
        action: {
          disabledBackground: '#f5f8fb',
          // disabledBackground: 'rgba(0, 0, 0, 0.175)',
        },
        text: {
          primary: 'rgba(0, 0, 0, 0.75)',
          hint: 'rgba(0, 0, 0, 0.3)',
        },
        info: {
          main: '#87F9F9',
          light: '#D0FCFC',
          dark: '#009FD4',
        },
        error: {
          main: '#EE4B4B',
          light: '#FFC0C2',
        },
      },

      customTheme?.palette,
    ),
  });

  // Adding custom color for dark bg's
  palette.dark = {
    contrastText: palette.getContrastText(palette.dark.main),
    dark: darken(palette.dark.main, 0.2),
    light: lighten(palette.dark.main, 0.2),
    main: palette.dark.main,
    ...palette?.dark,
  };

  /**
   * Typography
   */
  const fontFamilyBody = [
    ...(customTheme?.typography?.fontFamilyBody || []),
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
  ].join(',');

  const fontFamilyAccent = [
    ...(customTheme?.typography?.fontFamilyAccent || []),
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
  ].join(',');

  const typography = merge(
    {
      fontFamily: fontFamilyBody,
      fontFamilyAccent,

      h1: {
        fontFamily: fontFamilyAccent,
        fontWeight: 700,
        fontSize: '2.5rem',
        lineHeight: '1',
      },
      h2: {
        fontFamily: fontFamilyAccent,
        fontWeight: 700,
        fontSize: '1.875rem',
      },
      h3: {
        fontFamily: fontFamilyAccent,
        fontWeight: 400,
        fontSize: '1.5rem',
      },
      h4: {
        fontFamily: fontFamilyBody,
        fontWeight: 400,
        fontSize: '1.5rem',
      },
      h5: {
        fontFamily: fontFamilyAccent,
        fontWeight: 600,
        fontSize: '1.25rem',
        textTransform: 'uppercase',
      },
      h6: {
        fontFamily: fontFamilyAccent,
        fontWeight: 'bold',
        fontSize: '1rem',
        textTransform: 'uppercase',
      },
      button: {
        fontFamily: fontFamilyAccent,
        fontWeight: 600,
        textTransform: 'none',
      },
      overline: {
        fontFamily: fontFamilyAccent,
        fontWeight: 'bold',
      },
      caption: {
        lineHeight: '1.4',
      },
    },
    customTheme?.typography,
  );

  delete typography.fontFamilyBody;
  typography.fontFamily = fontFamilyBody;
  typography.fontFamilyAccent = fontFamilyAccent;

  /**
   * Other
   */
  const shape = merge(
    {
      borderRadius: 4,
    },
    customTheme?.shape,
  );

  const mixins = merge(
    {
      toolbar: { minHeight: 56 },
    },
    customTheme?.mixins,
  );

  /**
   * Setting default props for all MUI components
   */
  const components = merge(
    {
      MuiContainer: {
        defaultProps: {
          // fixed: true,
          // maxWidth: false,
        },
        styleOverrides: {
          root: {
            paddingLeft: defaultTheme.spacing(2),
            paddingRight: defaultTheme.spacing(2),

            [breakpoints.up('sm')]: {
              paddingLeft: defaultTheme.spacing(3),
              paddingRight: defaultTheme.spacing(3),
            },
          },
          fixed: {
            [breakpoints.up('pt')]: {
              maxWidth: 'none',
            },
            [breakpoints.up('sm')]: {
              maxWidth: `${680 + parseInt(defaultTheme.spacing(4), 10) * 2}px`,
            },
            [breakpoints.up('md')]: {
              maxWidth: `${920 + parseInt(defaultTheme.spacing(4), 10) * 2}px`,
            },
            [breakpoints.up('lg')]: {
              maxWidth: `${1146 + parseInt(defaultTheme.spacing(4), 10) * 2}px`,
            },
          },
          maxWidthLg: {
            [breakpoints.up('lg')]: {
              maxWidth: `${1146 + parseInt(defaultTheme.spacing(4), 10) * 2}px`,
            },
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          square: true,
        },
        styleOverrides: {
          root: {
            boxShadow: 'none',
            marginTop: '-1px',
            marginBottom: '-1px',

            '&:nth-child(even)': {
              backgroundColor: '#f5f8fb',
            },

            '&.Mui-expanded': {
              margin: '-1px 0',
            },

            '&:before': {
              display: 'none',
            },
          },
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          expandIcon: <BlIcon icon="arrow-down" fontSize="large" />,
        },
        styleOverrides: {
          root: {
            padding: '0px 20px',
            minHeight: '53px',

            '&.Mui-expanded': {
              minHeight: '53px',
            },
          },
          content: {
            color: palette.secondary.dark,
            textTransform: 'uppercase',
            fontWeight: 700,
            fontSize: '16px',
            fontFamily: typography.fontFamilyAccent,
            margin: 0,

            '&.Mui-expanded': {
              margin: 0,
            },
          },
          expandIconWrapper: {
            color: palette.dark.main,
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '0 20px 24px',
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          colorDefault: {
            backgroundColor: 'transparent',

            [breakpoints.up('sm')]: {
              backgroundColor: '#fff',
            },
          },
          colorSecondary: {
            backgroundColor: 'transparent',
            color: 'currentColor',

            [breakpoints.up('sm')]: {
              backgroundColor: palette.dark.main,
              color: palette.dark.contrastText,
            },
          },
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
        styleOverrides: {},
      },
      MuiCardActionArea: {
        defaultProps: {
          disableRipple: true,
          focusRipple: true,
        },
        styleOverrides: {},
      },
      MuiChip: {
        defaultProps: {
          deleteIcon: <BlIcon icon="close-thin" />,
        },
        styleOverrides: {
          root: {
            height: '36px',
            backgroundColor: '#f5f8fb',
            padding: '4px',
            maxWidth: '100%',
            fontSize: '1rem',
            lineHeight: '2rem',
            borderRadius: '100rem',
          },
          deleteIcon: {
            color: '#3d3d3d',

            '&.MuiIcon-root': {
              fontSize: '0.75rem',
              marginLeft: '-2px',
            },
          },
          label: {
            lineHeight: 1.8,
          },
        },
      },
      MuiLink: {
        defaultProps: {
          underline: 'always',
          color: 'primary',
        },
        styleOverrides: {
          root: {
            fontWeight: 600,
            transition: '0.2s ease',

            '&:hover': {
              opacity: 0.8,
              textDecoration: 'none',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          color: 'dark',
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            padding: '9px 24px',
            // minWidth: '150px',
          },

          text: {
            padding: '6px 8px',
          },

          textDark: {
            color: palette.dark.main,
          },

          contained: {
            '&.Mui-disabled': {
              backgroundColor: '#B4B4B4',
              color: '#fff',
            },
          },

          containedDark: {
            backgroundColor: palette.dark.main,
            color: palette.dark.contrastText,

            '&:hover': {
              backgroundColor: palette.dark.light,

              '@media (hover: none)': {
                backgroundColor: palette.dark.main,
              },
            },
          },

          outlined: {
            padding: '7px 22px',
            borderWidth: '2px',

            '&.Mui-disabled': {
              borderWidth: '2px',
            },
            // '&:hover': {
            //   border: `2px solid ${palette.dark.main}`,
            // },
          },

          outlinedDark: {
            border: `2px solid ${palette.dark.main}`,
            color: palette.dark.main,

            '&:hover': {
              borderWidth: '2px',
            },
          },

          outlinedPrimary: {
            border: `2px solid ${palette.primary.main}`,

            '&:hover': {
              borderWidth: '2px',
            },
          },
          outlinedSecondary: {
            border: `2px solid ${palette.secondary.main}`,

            '&:hover': {
              borderWidth: '2px',
            },
          },

          outlinedSizeSmall: {
            padding: '3px 9px',
            fontSize: '0.8125rem',
          },
          outlinedSizeLarge: {
            padding: '8px 30px',
            fontSize: '1rem',
          },
          containedSizeLarge: {
            padding: '10px 32px',
            fontSize: '1rem',
          },
          containedSizeSmall: {
            padding: '5.5px 14px',
            fontSize: '0.75rem',
          },
          textSizeSmall: {
            borderRadius: '100rem',
            padding: '4px 8px',
            fontSize: '0.8125rem',
          },
          textSizeLarge: {
            padding: '11px 15px',
            fontSize: '1rem',
          },

          startIcon: {
            marginRight: '5px',
          },

          endIcon: {
            marginLeft: '5px',
          },

          iconSizeSmall: {
            '& .BlIcon-root': {
              fontSize: '14px',
            },
          },

          iconSizeMedium: {
            '& .BlIcon-root': {
              fontSize: '16px',
            },
          },

          iconSizeLarge: {
            '& .BlIcon-root': {
              fontSize: '20px',
            },
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          popupIcon: <BlIcon icon="arrow-down" />,
        },
        styleOverrides: {
          root: {
            '& legend': {
              maxWidth: 0,
            },
          },
          popupIndicator: {
            marginRight: '4px',
            padding: '4px',
          },

          paper: {
            margin: 0,
            borderRadius: 0,
          },

          option: {
            '&[aria-selected="true"], &[data-focus="true"]': {
              backgroundColor: alpha(palette.primary.light, 0.4),

              // '&:hover': {
              //   backgroundColor: fade(palette.primary.light, 0.4),
              // },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          margin: 'normal',
          variant: 'outlined',
        },
        styleOverrides: {},
      },
      MuiFormHelperText: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            lineHeight: 1.2,
            marginTop: '5px',

            '&.Mui-error': {
              font: `700 0.75rem ${typography.fontFamily}`,
            },
          },
          contained: {
            marginLeft: 0,
          },
        },
      },
      MuiOutlinedInput: {
        defaultProps: {
          notched: false,
        },
        styleOverrides: {
          root: {
            borderRadius: 0,
            borderWidth: '1px',
            borderColor: palette.text.hint,
            backgroundColor: '#fff',

            '& .MuiOutlinedInput-notchedOutline': {
              borderWidth: '1px',
              borderColor: palette.text.hint,

              '& legend': {
                maxWidth: '0',
              },
            },

            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderColor: palette.dark.main,
              },

              '& .MuiTextFieldDivider-root': {
                color: palette.dark.main,
              },
            },

            '&.Mui-focused.Mui-error': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '1px',
                borderColor: palette.error.main,
              },

              '& .MuiTextFieldDivider-root': {
                color: palette.error.main,
              },
            },

            '&.Mui-error': {
              '& .MuiTextFieldDivider-root': {
                color: palette.error.main,
              },
            },
            '&.Mui-disabled': {
              backgroundColor: '#F5F8FB',
              color: palette.text.hint,
            },

            '&:hover': {
              '& .MuiTextFieldDivider-root': {
                color: palette.text.primary,
              },
            },
          },
          input: {
            height: '3.75rem',
            lineHeight: '1.4',
            padding: '19px 20px',
            boxSizing: 'border-box',
          },

          inputMultiline: {
            padding: 0,
          },

          adornedEnd: {
            paddingRight: '8px',
            alignItems: 'stretch',
          },

          multiline: {
            paddingLeft: '20px',
            paddingTop: '23px',
            paddingBottom: '15px',
          },

          label: {
            '&.Mui-error': {
              color: palette.error.main,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus':
              {
                WebkitBoxShadow: '0 0 0 1000px #e9f0fe inset!important',
                WebkitTextFillColor: 'inherit!important',
                caretColor: 'inherit',
              },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'default',
          icon: <CheckIcon />,
          checkedIcon: <CheckIcon checked />,
          indeterminateIcon: <IndeterminateCheckBoxSharp fontSize="small" />,
        },
        styleOverrides: {},
      },
      MuiRadio: {
        defaultProps: {
          color: 'default',
          icon: <RadioIcon />,
          checkedIcon: <RadioIcon checked />,
        },
        styleOverrides: {
          root: {
            color: palette.dark.main,

            '& .MuiSvgIcon-root:first-child': {
              opacity: 0.6,
            },

            '&.Mui-disabled .MuiSvgIcon-root:first-child': {
              opacity: 1,
            },

            '& + .MuiFormControlLabel-label': {
              borderRadius: '100rem',
              padding: '0.25rem 0.9rem',
              marginLeft: '-0.25rem',
            },

            '&.Mui-checked + .MuiFormControlLabel-label': {
              backgroundColor: alpha(palette.primary.light, 0.875),
              fontWeight: 600,

              ...(() => {
                if (rootProps?.id !== THEME_BL) return {};

                return {
                  backgroundColor: palette.secondary.light,
                };
              })(),
            },
            '&.Mui-checked + .MuiFormControlLabel-label.Mui-disabled': {
              backgroundColor: '#f5f8fb',
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          MenuProps: {
            // anchorEl: null,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
          },
          // eslint-disable-next-line react/prop-types
          IconComponent: ({ className }) => {
            return <BlIcon icon="arrow-down" className={className || ''} />;
          },
        },
        styleOverrides: {
          root: {
            paddingTop: '19px',
            paddingBottom: '19px',
          },

          icon: {
            top: 'unset',
            right: '20px',
            fontSize: '1rem',
            padding: 0,
            color: palette.dark.main,

            [breakpoints.up('lg')]: {
              right: '16px',
            },
          },

          outlined: {
            '&.MuiSelect-outlined': {
              paddingRight: '42px',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: alpha(palette.primary.light, 0.4),

              '&:hover': {
                backgroundColor: alpha(palette.primary.light, 0.4),
              },
            },
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          fontSizeSmall: {
            fontSize: '0.75rem',
          },
          fontSizeMedium: {
            fontSize: '1rem',
          },
          fontSizeLarge: {
            fontSize: '1.5rem',
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          color: 'primary',
        },
        styleOverrides: {
          root: {
            width: '64px',
            height: '38px',
            padding: '7px',
          },

          track: {
            borderRadius: '100rem',
          },

          thumb: {
            width: '18px',
            height: '18px',
            backgroundColor: '#fff',
          },

          switchBase: {
            padding: '10px',

            '&.Mui-checked': {
              transform: 'translateX(26px)',
            },

            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: palette.dark.main,
              opacity: 1,
            },

            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.12,
            },
          },

          colorPrimary: {
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: palette.primary.light,
            },
          },
          colorSecondary: {
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: palette.secondary.light,
            },
          },
        },
      },
      MuiSwitchChip: {
        defaultProps: {},
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              backgroundColor: alpha(palette.primary.light, 0.875),

              ...(() => {
                if (rootProps?.id !== THEME_BL) return {};

                return {
                  backgroundColor: palette.secondary.light,
                };
              })(),
            },
            '&.Mui-disabled': {
              backgroundColor: '#f5f8fb',
            },
          },
        },
      },
      MuiSlider: {
        defaultProps: {
          color: 'secondary',
          components: {
            ValueLabel: ValueLabelComponent,
          },
        },
        styleOverrides: {
          root: {
            height: '7px',
            padding: '13px 0',
            borderRadius: '1px',

            '@media(pointer: coarse)': {
              padding: '13px 0',
            },

            '& .MuiTooltip-popper': {
              zIndex: 10,
            },

            '& .MuiTooltip-tooltip': {
              fontSize: '1rem',
              fontFamily: fontFamilyAccent,
              fontWeight: 600,
              padding: '4px 6px 2px',
            },
          },

          colorSecondary: {
            color: palette.dark.main,

            '& .MuiSlider-thumb': {
              backgroundColor:
                rootProps.id === THEME_BL
                  ? palette.secondary.main
                  : palette.primary.main,
            },
          },

          rail: {
            height: 'inherit',
            color: '#B4B4B4',
            opacity: 1,
          },

          track: {
            height: 'inherit',
          },

          thumb: {
            width: '28px',
            height: '28px',
            // marginTop: '-10px',
            // marginLeft: '-14px',
            border: '8px solid currentColor',
            backgroundColor: palette.primary.light,
          },

          marked: {
            marginBottom: '30px',
          },

          markLabel: {
            top: '40px',
            fontSize: '1rem',
          },

          mark: {
            height: '16px',
            // top: '9px',
            backgroundColor: '#B4B4B4',
            opacity: '1',
            borderRadius: 0,
          },

          markActive: {
            backgroundColor: 'currentColor',
            opacity: 1,
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true,
          enterTouchDelay: 0,
        },
        styleOverrides: {
          tooltip: {
            fontSize: '0.875rem',
            fontWeight: 'normal',
            backgroundColor: '#fff',
            boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.12)',
            color: palette.dark.main,
            lineHeight: '1.2',
            padding: '10px 22px',
            border: `0.5px solid ${palette.action.disabledBackground}`,

            // borderRadius: '6px',
          },
          touch: {
            padding: '10px 22px',
            fontSize: '1rem',
          },
          arrow: {
            color: '#fff',

            '&::before': {
              position: 'relative',
              border: `0.5px solid ${palette.action.disabledBackground}`,
              zIndex: 1,
            },
          },
        },
      },
      MuiAlert: {
        defaultProps: {
          icon: false,
          variant: 'filled',
        },
        styleOverrides: {
          root: {
            position: 'relative',
            boxShadow: shadows[1],
            borderRadius: '0',
            textAlign: 'left',
            '& .MuiTypography-h6': {
              fontWeight: 400,
            },
          },
          filledSuccess: {
            color: palette.dark.main,
            backgroundColor: palette.success.light,
          },
          filledInfo: {
            color: palette.getContrastText(palette.info.light),
            backgroundColor: palette.info.light,
          },
          filledWarning: {
            color: palette.getContrastText(palette.warning.light),
            backgroundColor: palette.warning.light,
          },
          filledError: {
            color: palette.getContrastText(palette.error.light),
            backgroundColor: palette.error.light,
          },

          action: {
            flexShrink: 0,
          },
        },
      },
      MuiDataGrid: {
        defaultProps: {
          autoHeight: true,
          disableColumnMenu: true,
          disableRowSelectionOnClick: true,
          pageSize: 10,
          rowHeight: 60,
        },
        styleOverrides: {
          root: {
            border: 'none',
            fontSize: '1rem',
            maxWidth: '100%',
            overflow: 'visible',
            '--DataGrid-rowBorderColor': 'transparent',
            '--DataGrid-containerBackground': 'transparent',
            [`
            & .MuiDataGrid-columnHeader:focus, 
            & .MuiDataGrid-cell:focus, 
            & .MuiDataGrid-columnHeader:focus-within, 
            & .MuiDataGrid-cell:focus-within
          `]: {
              outline: 'none',
            },
            '& .MuiDataGrid-withBorderColor': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeader': {
              paddingLeft: '24px',
              '&:first-child:not(.MuiDataGrid-columnHeaderCheckbox)': {
                paddingLeft: '30px',
              },

              '&:last-child': {
                paddingRight: '30px',
              },
            },

            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              display: 'flex',
              alignItems: 'center',
              paddingLeft: '24px',
              '&:first-child:not(.MuiDataGrid-cellCheckbox)': {
                paddingLeft: '30px',
              },

              '&:last-child': {
                paddingRight: '30px',
              },
            },

            '& .MuiDataGrid-main': {
              paddingBottom: '1rem',
              backgroundColor: palette.background.paper,
              boxShadow: shadows[1],
              borderRadius: 'inherit',
            },

            '& .MuiDataGrid-columnsContainer': {
              paddingTop: '1rem',
              borderBottom: 'none',
            },

            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },

            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '0.75rem',
              fontFamily: typography.fontFamilyAccent,
              color: palette.dark.main,
              textTransform: 'uppercase',
              fontWeight: 700,
            },

            '& .MuiDataGrid-columnHeaderTitleContainer': {
              padding: 0,
            },
            '& .MuiButtonBase-root.Mui-checked .MuiSvgIcon-root': {
              fill: palette.dark.main,
            },

            '& .MuiDataGrid-iconButtonContainer': {
              display: 'flex',
              alignItems: 'center',
            },

            '& .MuiDataGrid-footerContainer': {
              marginTop: '30px',
            },

            '& .MuiTablePagination-root': {
              marginLeft: 'auto',
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          root: {
            '& .MuiDialogTitle-root': {
              color: palette.dark.main,
              // paddingRight: '54px',
            },
            '& .MuiDialogTitle-caption': {
              color: '#3d3d3d',
            },
            '& .MuiDialog-paperFullScreen': {
              display: 'inline-flex',
              flexDirection: 'column',
            },
          },
          scrollBody: {
            '&::after': {
              display: 'none',
            },
          },

          paperScrollBody: {
            display: 'inline-flex',
            flexDirection: 'column',

            '& .MuiDialogContent-root': {
              overflowY: 'visible',
            },
          },
        },
      },
      MuiDialogTitle: {
        defaultProps: {
          TypographyProps: {
            variant: 'h5',
            component: 'h5',
          },
        },
        // styleOverrides: {
        // root: {
        //   paddingRight: '54px',
        //   color: palette.dark.main,
        // },
        // caption: {
        //   color: '#3d3d3d',
        // },
        // },
      },
      MuiDialogContent: {
        defaultProps: {},
        styleOverrides: {
          root: {
            paddingBottom: '30px',
          },
        },
      },
      MuiDialogActions: {
        defaultProps: {},
        styleOverrides: {
          root: {
            padding: '8px 24px 24px',
          },
        },
      },
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
        styleOverrides: {},
      },
      MuiBreadcrumbs: {
        defaultProps: {
          separator: (
            <BlIcon
              icon="double-arrow-right"
              className="Breadcrumbs-separatorIcon"
            />
          ),
        },
        styleOverrides: {
          root: {
            fontSize: '12px',
          },
          li: {
            lineHeight: 1,
            padding: '3px 0 6px',
            '& a': {
              color: palette.text.secondary,
            },
            '&:last-child a': {
              color: palette.dark.main,
            },
          },
          separator: {
            color: palette.dark.light,
            '& .Breadcrumbs-separatorIcon': {
              width: '11px',
              fontSize: '11px',
            },
          },
        },
      },
      MuiExample: {
        defaultProps: {},
        styleOverrides: {},
      },

      // Pickers

      MuiPickersLayout: {
        styleOverrides: {
          root: {
            minWidth: '260px',
          },
        },
      },

      MuiPickersBasePicker: {
        styleOverrides: {
          pickerView: {
            minWidth: ' ',
            minHeight: 0,
          },
        },
      },

      MuiDateCalendar: {
        styleOverrides: {
          root: {
            height: 'auto',
            width: 'auto',
            margin: 'auto',
          },
        },
      },

      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            position: 'relative',
            marginTop: 0,
            marginBottom: 0,
            paddingLeft: 0,
            paddingRight: 0,
            maxHeight: '44px',
            minHeight: '44px',
          },

          labelContainer: {
            width: '100%',
            justifyContent: 'center',
          },

          label: {
            textTransform: 'uppercase',
            color: palette.dark.main,
            fontFamily: fontFamilyAccent,
            fontWeight: 'bold',
            marginRight: 0,
          },

          switchViewButton: {
            display: 'none',
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          root: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            pointerEvents: 'none',
          },
          button: {
            padding: '8px',
            margin: '2px',
            borderRadius: shape.borderRadius,
            pointerEvents: 'auto',
          },
          spacer: {
            width: '100%',
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          header: {
            borderBottom: '1px solid rgba(125,125,125, 0.25)',
            paddingBottom: '0.5rem',
            marginBottom: '2px',
            marginTop: '-8px',
            '.MuiDayCalendar-weekDayLabel': {
              flex: 1,
            },
          },
          weekContainer: {
            margin: 0,
          },
          weekDayLabel: {
            height: 'auto',
            width: '32px',
            color: palette.action.disabled,
            lineHeight: 1,
          },
          slideTransition: {
            minHeight: '200px',
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            width: '28px',
            height: '28px',
            margin: '2px 4px',
            fontSize: '14px',
            fontWeight: 500,

            '&.Mui-selected': {
              backgroundColor: palette.dark.main,

              '&:hover,&:focus': {
                backgroundColor: palette.dark.light,
              },
            },
          },
          today: {
            color: palette.text.primary,
            fontWeight: 600,

            '&:not(.Mui-selected)': {
              border: 'none',
              borderBottom: `2px solid ${palette.dark.main}`,
            },
          },
        },
      },
      MuiDateRangePickerDay: {
        styleOverrides: {
          root: {
            flex: 1,
            backgroundColor: 'transparent',
            '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
              backgroundColor: alpha(palette.primary.light, 0.4),
              '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
                paddingLeft: 0,
                borderLeft: '4px solid #fff',
              },
              '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
                backgroundColor: 'transparent',
                paddingLeft: 0,
                paddingRight: 0,
                borderRight: '4px solid #fff',
                borderLeft: `4px solid ${alpha(palette.primary.light, 0.4)}`,
              },
            },
            '&.MuiDateRangePickerDay-endOfMonth': {
              paddingRight: '4px',
            },
          },
          day: {},
        },
      },
      MuiListSubheader: {
        defaultProps: {},
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiListItemIcon: {
        defaultProps: {},
        styleOverrides: {
          root: {
            width: '32px',
            marginRight: '8px',
            minWidth: '0',
          },
        },
      },
      MuiFormLabel: {
        defaultProps: {},
        styleOverrides: {
          root: {
            '&.Mui-focused': {
              color: palette.dark.main,
            },
          },
        },
      },
      MuiInputAdornment: {
        defaultProps: {},
        styleOverrides: {
          root: {
            flexShrink: 0,
            maxHeight: 'none',
            height: 'auto',
          },
        },
      },
      MuiInputLabel: {
        defaultProps: {},
        styleOverrides: {
          outlined: {
            color: palette.text.hint,
            textTransform: 'capitalize',
            lineHeight: 1,
            transform: 'translate(20px, 22px) scale(1)',

            '&.MuiInputLabel-shrink': {
              transform: 'translate(20px, 6px) scale(0.75)',
              fontWeight: 'bold',
              textTransform: 'lowercase',
              color: palette.dark.main,
            },
            '&.Mui-disabled': {
              color: palette.text.hint,
            },
            '&.Mui-error': {
              color: palette.error.main,
            },

            '& + .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': {
              '& .MuiOutlinedInput-input': {
                paddingTop: '23px',
                paddingBottom: '15px',
              },
            },
            '& + .MuiInputBase-multiline .MuiOutlinedInput-input': {
              padding: 0,
              height: '22px',
            },
          },
        },
      },
      MuiTableRow: {
        defaultProps: {},
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
          },
        },
      },
      MuiTableCell: {
        defaultProps: {},
        styleOverrides: {
          root: {
            color: palette.text.primary,
            lineHeight: '1.5',
            borderBottom: 'none',
            fontSize: '1rem',

            '&.MuiTableCell-head': {
              fontSize: '0.75rem',
              color: palette.dark.main,
              textTransform: 'uppercase',
              fontWeight: 'bold',
            },
          },
        },
      },
      MuiTablePagination: {
        defaultProps: {},
        styleOverrides: {
          actions: {
            marginLeft: '0.5rem',

            '& .MuiIconButton-root': {
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #b4b4b4',
              borderLeftWidth: 0,

              '&:not(:first-child)': {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              },

              '&:not(:last-child)': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderLeftWidth: '1px',
              },
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        defaultProps: {},
        styleOverrides: {
          groupedHorizontal: {
            '&:not(:first-child)': {
              borderLeft: 0,
            },
          },
        },
      },
      MuiToggleButton: {
        defaultProps: {},
        styleOverrides: {
          root: {
            backgroundColor: '#fff',
            color: palette.text.secondary,

            '&.Mui-selected': {
              backgroundColor: palette.dark.main,
              color: palette.dark.contrastText,

              '&:hover': {
                backgroundColor: palette.dark.light,
              },
            },
          },
        },
      },
      MuiIconButton: {
        defaultProps: {},
        styleOverrides: {
          root: {
            color: palette.dark.main,
          },
          // Fixes override of color='inherit' when we redefined it in root
          colorInherit: {
            color: 'inherit',
          },
          sizeMedium: {
            padding: '12px',
          },
        },
      },

      MuiStepper: {
        styleOverrides: {
          root: {
            background: 'none',
            border: 'none',
            padding: '20px 0',
          },
          horizontal: {
            justifyContent: 'space-between',

            '& .MuiStepLabel-labelContainer': {
              display: 'none',
            },

            [breakpoints.up('md')]: {
              '& .MuiStepConnector-root': {
                display: 'none',
              },

              '& .MuiStepLabel-labelContainer': {
                display: 'inline-flex',
              },
            },
          },
        },
      },
      MuiStep: {
        styleOverrides: {
          root: {
            [breakpoints.up('md')]: {
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              flexBasis: 0,
              backgroundColor: '#fff',
              border: `1px solid ${palette.action.disabledBackground}`,
              height: '50px',
              marginRight: '2px',

              '&:last-child': {
                marginRight: '0',
              },
            },
          },
          horizontal: {
            paddingLeft: '0',
            paddingRight: '0',

            [breakpoints.up('md')]: {
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            },
          },
        },
      },

      MuiStepLabel: {
        defaultProps: {
          StepIconComponent: StepIcon,
        },
        styleOverrides: {
          label: {
            fontWeight: 'bold',
            fontSize: '1rem',

            '&.Mui-completed': {
              fontWeight: 'bold',
            },

            '&.Mui-active': {
              fontWeight: 'bold',
            },

            [breakpoints.up('md')]: {
              fontSize: '0.875rem',
            },
          },
          iconContainer: {
            paddingRight: '0',
          },
          labelContainer: {
            paddingLeft: '12px',
          },
        },
      },

      MuiStepButton: {
        styleOverrides: {
          root: {
            boxSizing: 'border-box',
            margin: '-0.5rem -1rem',
            padding: '0.5rem 1rem',
            width: 'calc(100% + 2rem)',
            justifyContent: 'flex-start',
          },
        },
      },

      MuiStepConnector: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: 0.375,
            },
          },

          line: {
            borderColor: 'currentColor',
          },

          lineHorizontal: {
            borderTopWidth: '0.5rem',
          },
        },
      },
    },

    customTheme?.components,
  );

  shadows.forEach((shadow, index) => {
    defaultTheme.shadows[index] = shadow;
  });

  let theme = createMuiTheme({
    cssVariables: true,
    ...rootProps,
    breakpoints: { values: breakpoints.values },
    palette,
    typography,
    shape,
    mixins,
    components,
    shadows: defaultTheme.shadows,
  });

  theme = { custom: rootCustom, ...theme };

  // if (NODE_ENV === 'development') {
  window.theme = theme;
  // }

  return theme;
};

const ValueLabelPopperProps = {
  disablePortal: true,
};

function ValueLabelComponent(props) {
  // eslint-disable-next-line react/prop-types
  const { children, open, value } = props;

  return (
    <Tooltip
      open={open}
      enterTouchDelay={0}
      placement="top"
      title={value}
      PopperProps={ValueLabelPopperProps}
    >
      {children}
    </Tooltip>
  );
}

export default createTheme;
